import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "./ui/button";
import { SRMark } from "./SRMark";

const NAV = [
  { to: "/gp-practices", label: "For GP Practices" },
  { to: "/psychologists", label: "Psychologists" },
  { to: "/psychiatrists", label: "Psychiatrists" },
  { to: "/pricing", label: "Pricing" },
  { to: "/contact", label: "Contact" },
];

const FOOTER = [
  { to: "/gp-practices", label: "GP Practices" },
  { to: "/psychologists", label: "Psychologists" },
  { to: "/psychiatrists", label: "Psychiatrists" },
  { to: "/pricing", label: "Pricing" },
  { to: "/privacy", label: "Privacy" },
  { to: "/terms", label: "Terms" },
  { to: "/contact", label: "Contact" },
];

export const MarketingLayout = ({ children, active }) => (
  <div className="min-h-screen bg-white text-slate-900">
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3.5 sm:px-8">
        <Link to="/" className="flex items-center gap-2.5" data-testid="marketing-logo">
          <SRMark />
          <span className="font-heading text-lg font-semibold tracking-tight text-slate-900">SimplyRefer</span>
        </Link>
        <nav className="hidden items-center gap-6 text-sm font-medium text-slate-600 md:flex">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className={`hover:text-primary ${active === n.to ? "text-primary" : ""}`}
              data-testid={`nav-${n.to.replace("/", "")}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" data-testid="nav-signin">
            <Link to="/login">Sign in</Link>
          </Button>
          <Button asChild variant="brand" size="sm" data-testid="nav-create-account">
            <Link to="/signup">Create account</Link>
          </Button>
        </div>
      </div>
    </header>

    <main>{children}</main>

    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2.5">
              <SRMark />
              <span className="font-heading text-base font-semibold text-slate-900">SimplyRefer Australia</span>
            </div>
            <p className="mt-2 text-sm text-slate-500">Making referrals simpler.</p>
          </div>
          <nav className="flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-600">
            {FOOTER.map((f) => (
              <Link key={f.to} to={f.to} className="hover:text-primary" data-testid={`footer-${f.to.replace("/", "")}`}>
                {f.label}
              </Link>
            ))}
            <Link to="/login" className="hover:text-primary" data-testid="footer-signin">Sign in</Link>
          </nav>
        </div>
        <p className="mt-10 text-xs text-slate-400">© {new Date().getFullYear()} SimplyRefer. An Australian mental health referral platform.</p>
      </div>
    </footer>
  </div>
);

// Reusable section primitives to keep the pages calm and consistent.
export const Hero = ({ eyebrow, h1, sub, primary, secondary }) => (
  <section className="border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white">
    <div className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-20">
      {eyebrow && <p className="text-xs font-semibold uppercase tracking-wide text-primary">{eyebrow}</p>}
      <h1 className="mt-2 font-heading text-3xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">{h1}</h1>
      <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">{sub}</p>
      {(primary || secondary) && (
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {primary && (
            <Button asChild size="lg" variant="brand" data-testid="hero-primary-cta">
              <Link to={primary.to}>{primary.label}</Link>
            </Button>
          )}
          {secondary && (
            <Button asChild size="lg" variant="brand-outline" data-testid="hero-secondary-cta">
              <Link to={secondary.to}>{secondary.label}</Link>
            </Button>
          )}
        </div>
      )}
    </div>
  </section>
);

export const Section = ({ title, children, muted }) => (
  <section className={muted ? "bg-slate-50" : "bg-white"}>
    <div className="mx-auto max-w-4xl px-5 py-12 sm:px-8">
      {title && <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">{title}</h2>}
      <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">{children}</div>
    </div>
  </section>
);

// Small benefit cards (title, optional description, calm styling).
export const BenefitCards = ({ items }) => (
  <section className="bg-white">
    <div className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="benefit-cards">
        {items.map((it) => {
          const title = typeof it === "string" ? it : it.title;
          const desc = typeof it === "string" ? null : it.desc;
          const highlight = typeof it === "string" ? false : !!it.highlight;
          return (
            <div key={title} className="rounded-xl border-2 border-primary bg-white p-5">
              <Check className="h-5 w-5 text-primary" />
              <p className="mt-3 text-sm font-bold text-slate-900">{title}</p>
              {desc && <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{desc}</p>}
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

// Numbered "how it works" steps.
export const Steps = ({ title = "How it works", steps }) => (
  <section className="bg-slate-50">
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8">
      <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">{title}</h2>
      <ol className="mt-6 space-y-4" data-testid="how-it-works">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">{i + 1}</span>
            <span className="pt-0.5 text-slate-700">{s}</span>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

// Simple FAQ (native disclosure, no dependency).
export const Faq = ({ items }) => (
  <section className="bg-white">
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8">
      <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Frequently asked questions</h2>
      <div className="mt-6 divide-y divide-slate-200 border-y border-slate-200" data-testid="faq">
        {items.map((f, i) => (
          <details key={i} className="group py-4">
            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-slate-900">
              {f.q}
              <span className="ml-4 text-slate-400 transition-transform group-open:rotate-45">+</span>
            </summary>
            <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{f.a}</p>
          </details>
        ))}
      </div>
    </div>
  </section>
);

// Final call-to-action band.
export const FinalCta = ({ heading, primary, secondary }) => (
  <section className="bg-slate-50">
    <div className="mx-auto max-w-3xl px-5 py-14 text-center sm:px-8">
      <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">{heading}</h2>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Button asChild size="lg" variant="brand" data-testid="final-cta-primary">
          <Link to={primary.to}>{primary.label}</Link>
        </Button>
        {secondary && (
          <Button asChild size="lg" variant="brand-outline" data-testid="final-cta-secondary">
            <Link to={secondary.to}>{secondary.label}</Link>
          </Button>
        )}
      </div>
    </div>
  </section>
);

// Small callout note (e.g. clinic / practice manager info). Brief, on-brand.
export const Note = ({ title, children }) => (
  <section className="bg-white">
    <div className="mx-auto max-w-3xl px-5 pb-12 sm:px-8">
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="marketing-note">
        <p className="text-sm font-semibold text-slate-900">{title}</p>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{children}</p>
      </div>
    </div>
  </section>
);